import { cva } from '@vizit/theme';
import * as React from 'react';
import { Group as AriaGroup, type GroupProps as AriaGroupProps, composeRenderProps } from 'react-aria-components';

interface GroupProps extends AriaGroupProps {}

const classes = cva({
  base: [
    'flex h-22 items-center gap-4 overflow-hidden text-primary',
    'min-w-0',
    'bg-primary',
    'border border-primary',
    'px-6 py-5',
    // Add focus-within:px-[11px] to reduce the horizontal padding by 1px when the border grows to 2px (11px instead of 12px)
    // Without this, the icon / placeholder would shift by 1px on focus
    'focus-within:outline-none focus-within:border-2 focus-within:border-brand-primary focus-within:px-[11px]',
    'disabled:cursor-not-allowed disabled:bg-disabled-subtle disabled:border-disabled disabled:text-disabled',
    '[&[data-invalid]:focus-within]:border-error-secondary [&[data-invalid]]:border-error-secondary',
    'forced-colors:bg-[Field]',
  ],
});

/**
 * The FieldGroup component is used to group fields together.
 *
 * @param props - The props for the FieldGroup component.
 * @example
 * <FieldGroup isDisabled={isSubmitting}>
 *   <MyTextField label="Name" />
 *   <MyTextField label="Email" />
 *   <CreditCardFields />
 * </FieldGroup>
 */
function FieldGroup(props: GroupProps, ref: React.ForwardedRef<HTMLDivElement>) {
  const { className: classNameProp, ...other } = props;

  return (
    <AriaGroup
      ref={ref}
      className={composeRenderProps(classNameProp, (className, renderProps) => classes({ ...renderProps, className }))}
      {...other}
    />
  );
}

const _FieldGroup = React.forwardRef<HTMLDivElement, GroupProps>(FieldGroup);

export type { GroupProps };
export { _FieldGroup as FieldGroup };
